import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useDndContext } from '@dnd-kit/core';
import { IconWrapper, SvgIcon } from '../../Icon';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../ActionsVisibilityContext';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDndSortableInternalContext } from '../../DndSortable/context';
import { useBoolean } from '@fluentui/react-hooks';
import ColumnFilterWidget from '../../MultiGridTable/ColumnFilterWidget';
import {
  ColumnFilterConfig,
  ColumnSortConfig,
} from '../../MultiGridTable/ColumnFilterWidget/types';
import { headerCellStyles } from './ChartAnalysisTableUI';
import { theme } from '../../../styles/theme';

interface IProps {
  id: string;
  columnIndex: number;
  children: React.ReactNode;
  isOver?: boolean;
  style: React.CSSProperties;
  onChangeFilter: (
    index: number,
    filterConfig: ColumnFilterConfig | null,
  ) => void;
  onChangeSort: (index: number, sortConfig: ColumnSortConfig | null) => void;
  filterConfig: ColumnFilterConfig | null;
  sortConfig: ColumnSortConfig | null;
  columnValues: (string | number)[];
  isResizing?: boolean;
}

const Container = styled.div<{
  $columnIndex: number;
}>`
  ${headerCellStyles};
  height: 100%;
  padding: 8px 0 8px 6px;
`;

const HandleWrapper = styled.button`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  cursor: move;
  color: ${theme.colors.secondary800};
  pointer-events: auto; /* Ensure the handle is draggable */

  .icon {
    font-size: 20px;
    line-height: 1;
    margin: 0;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  pointer-events: auto; /* Ensure children are clickable */
`;

const DraggableHeaderCell = ({
  id,
  columnIndex,
  children,
  isOver,
  style,
  onChangeFilter,
  onChangeSort,
  filterConfig,
  sortConfig,
  columnValues,
  isResizing,
}: IProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id,
      transition: {
        duration: 150, // milliseconds
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      },
    });

  const { active } = useDndContext();
  const { actionsVisible, showActions, hideActions } =
    useActionsVisibilityContext();
  const { setCurrentDragHandle, disabled } = useDndSortableInternalContext();

  const [isMenuVisible, { setTrue: showMenu, setFalse: hideMenu }] =
    useBoolean(false);
  const anchorEl = useRef<HTMLDivElement>(null);

  const showHandle = useMemo(() => {
    if (active?.data?.current?.sortable) {
      const curIndex = active.data.current.sortable.index;
      return curIndex === columnIndex;
    }
    return actionsVisible;
  }, [active, actionsVisible, columnIndex]);

  const onMouseEnter = () => {
    showActions();
    setCurrentDragHandle(disabled ? null : id);
  };

  const onMouseLeave = () => {
    if (!isMenuVisible) {
      hideActions();
      setCurrentDragHandle(null);
    }
  };

  const dynamicStyle = {
    ...style,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    backgroundColor: isOver ? 'rgba(0, 123, 255, 0.2)' : undefined,
  };

  const toggleMenuAndStopPropagation = useCallback(
    (e: React.MouseEvent) => {
      if (isMenuVisible) {
        hideMenu();
      } else {
        showMenu();
      }
      e.stopPropagation();
    },
    [isMenuVisible, showMenu, hideMenu],
  );

  const handleChangeFilter = (filterConfig: ColumnFilterConfig | null) => {
    onChangeFilter(columnIndex, filterConfig);
  };

  const handleChangeSort = (sortConfig: ColumnSortConfig | null) => {
    onChangeSort(columnIndex, sortConfig);
  };

  const handleClose = () => {
    hideMenu();
    hideActions();
  };

  const isFilterVisible = filterConfig?.columnIndex === columnIndex;

  const onClickFilter = () => {
    showActions();
    showMenu();
  };

  return (
    <Container
      style={dynamicStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      $columnIndex={columnIndex}
      ref={setNodeRef}
      {...attributes}
    >
      {showHandle && !isResizing && (
        <HandleWrapper {...listeners}>
          <SvgIcon
            name="drag-drop"
            width={8}
            height={8}
            customFill="currentColor"
          />
        </HandleWrapper>
      )}
      <ContentWrapper>{children}</ContentWrapper>

      {isFilterVisible && (
        <IconWrapper size={16} onClick={onClickFilter}>
          <SvgIcon name="filter" width={12} height={12} isActive={true} />
        </IconWrapper>
      )}

      {actionsVisible && !isResizing && (
        <IconWrapper
          ref={anchorEl}
          size={20}
          onClick={toggleMenuAndStopPropagation}
        >
          <SvgIcon
            name="dots-three-outline-vertical"
            width={20}
            height={20}
            isActive={isMenuVisible}
            isDisabled={disabled}
          />
        </IconWrapper>
      )}

      {isMenuVisible && (
        <ColumnFilterWidget
          anchorEl={anchorEl}
          isOpen={isMenuVisible}
          onClose={handleClose}
          onChangeFilter={handleChangeFilter}
          onChangeSort={handleChangeSort}
          sortConfig={sortConfig}
          filterConfig={filterConfig}
          columnValues={columnValues}
        />
      )}
    </Container>
  );
};

export default withActionsVisibilityContext(DraggableHeaderCell);
